<template>
  <section class="filter" :class="{ 'filter--open': showFilterOptions }">
    <!-- Filter Dropdown -->
    <div class="filter-dropdown" v-bind="$attrs" v-if="showFilterOptions">
      <div class="filter-dropdown__header">
        <h3 class="filter-dropdown__title">Filter {{ title }}</h3>
        <button class="filter-dropdown__close" @click="$emit('filter-click')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <!-- Only when more than one group is passed; a single-group filter keeps its old shape. -->
      <div v-if="groups.length > 1" class="filter-tabs" role="tablist">
        <button
          v-for="(group, index) in groups"
          :key="group.label"
          type="button"
          role="tab"
          class="filter-tabs__tab"
          :class="{ 'filter-tabs__tab--active': index === activeGroupIndex }"
          :aria-selected="index === activeGroupIndex"
          @click="selectGroup(index)"
        >
          {{ group.label }}
        </button>
      </div>

      <div class="filter-dropdown__options">
        <label
          :for="option"
          class="filter-option"
          v-for="(option, index) in activeGroup.options"
          :key="option + index"
        >
          <span
            v-if="activeGroup.showIndicators !== false"
            class="filter-option__indicator"
            :class="'filter-option__indicator--' + option.toLowerCase()"
          ></span>
          <span class="filter-option__label">{{ option }}</span>
          <input
            class="filter-option__radio"
            v-model="filterValue"
            type="radio"
            name="status"
            :value="option"
            :id="option"
          />
          <span class="filter-option__check" :class="{ 'filter-option__check--active': filterValue === option }">
            <svg v-if="filterValue === option" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </span>
        </label>
      </div>

      <div class="filter-dropdown__actions">
        <button class="filter-btn filter-btn--secondary" @click="clearOptions">
          Clear
        </button>
        <button
          class="filter-btn filter-btn--primary"
          @click="processFilterSelection"
          :disabled="(!filterValue && !isDateValid) || (!isDateValid && !isFilteredByStatus)"
        >
          Apply Filter
        </button>
      </div>
    </div>

    <!-- Filter Trigger Button -->
    <button class="filter-trigger" @click="$emit('filter-click')">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
      </svg>
    </button>
  </section>
</template>

<script>
  import Checkbox from "./Checkbox";
  import CustomButton from "./BtnComponent";
  import Datepicker from "vue3-datepicker";
  import FilterModal from "@/components/general/sidemodal/SideModal";
  import InputFieldLabel from "./InputFieldLabel";
  import Dropdown from "./Dropdown";
  import { industries } from "@/utils/helpers";
  export default {
    name: "Filter",
    emits: ["filter-click", "filter-selected"],
    data() {
      return {
        industries,
        filterValue: "",
        startDate: new Date(undefined),
        endDate: new Date(undefined),
        filterTypes: [
          {
            name: "Status",
            value: "status",
          },
          {
            name: "Date",
            value: "updatedAt",
          },
        ],
        filter: "",
        activeGroupIndex: 0,
      };
    },
    props: {
      showFilterOptions: {
        type: Boolean,
        required: true,
        default: () => false,
      },
      filterOptions: {
        type: Array,
        required: true,
      },
      title: {
        type: String,
        required: true,
      },
      // The coloured dot only carries meaning for statuses, where the colour maps to a state.
      // Filters over categories have no such mapping, so they get the label alone rather than
      // an empty circle implying one.
      showIndicators: {
        type: Boolean,
        required: false,
        default: () => true,
      },
      // Several filters in one dropdown, shown as tabs: [{ label, type, options,
      // showIndicators }]. Left empty, the component behaves exactly as it always has, driven
      // by `title` and `filterOptions` — every other caller is unaffected.
      filterGroups: {
        type: Array,
        required: false,
        default: () => [],
      },
    },
    components: {
      Checkbox,
      Datepicker,
      CustomButton,
      FilterModal,
      InputFieldLabel,
      Dropdown,
    },
    watch: {
      filterValue(val) {
        if (val && val.length > 0) {
          this.startDate = new Date(undefined);
          this.endDate = new Date(undefined);
        }
      },
      startDate(val) {
        if (String(val) !== "Invalid Date") {
          this.filterValue = "";
        }
      },
      endDate(val) {
        if (String(val) !== "Invalid Date") {
          this.filterValue = "";
        }
      },
    },
    computed: {
      groups() {
        if (this.filterGroups.length) return this.filterGroups;
        return [
          {
            label: this.title,
            type: this.getType(this.title),
            options: this.filterOptions,
            showIndicators: this.showIndicators,
          },
        ];
      },
      activeGroup() {
        return this.groups[this.activeGroupIndex] || this.groups[0];
      },
      isFilteredByStatus() {
        return !!this.filterValue;
      },
      isDateValid() {
        return (
          String(this.startDate) !== "Invalid Date" &&
          String(this.endDate) !== "Invalid Date"
        );
      },
    },
    methods: {
      clearOptions() {
        this.filterValue = "All";
        this.$emit("filter-selected", {
          value: "",
        });
      },
      // Only one filter applies at a time, so a selection made in another tab no longer means
      // anything once you switch — leaving it checked would suggest two filters were active.
      selectGroup(index) {
        if (index === this.activeGroupIndex) return;
        this.activeGroupIndex = index;
        this.filterValue = "";
      },
      processFilterSelection() {
        if (this.isFilteredByStatus) {
          const type = this.activeGroup.type;
          // "Active" is the label the status list has always shown for an approved deal. It is
          // a status-only alias, so it must not rewrite an option in another tab.
          const value =
            type === "status" && this.filterValue === "Active"
              ? "Approved"
              : this.filterValue;
          this.$emit("filter-selected", { value, type });
        } else {
          this.$emit("filter-selected", {
            value: {
              from: new Date(this.startDate).toISOString(),
              to: new Date(this.endDate).toISOString(),
            },
          });
        }
        this.$emit("filter-click");
      },
      getType(title) {
        let type = null;
        switch (title) {
          case "Deals":
            type = "status";
            break;

          case "Offering":
            type = "offering_type";
            break;

          default:
            break;
        }
        return type;
      },
    },
  };
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;
$btn-primary: #001240;
$btn-primary-hover: #06206B;
$success: #10B981;
$warning: #D97706;
$error: #DC2626;

.filter {
  position: relative;
  display: flex;
  align-items: center;

  &--open {
    z-index: 50;
  }
}

.filter-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 8px;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $accent-primary;
    color: $accent-primary;
  }
}

.filter-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 280px;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.12);
  animation: dropdownFade 0.2s ease;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 16px 12px;
    border-bottom: 1px solid $border-color;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $text-primary;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: transparent;
    border: none;
    border-radius: 6px;
    color: $text-muted;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: $bg-subtle;
      color: $text-primary;
    }
  }

  &__options {
    padding: 12px 16px;
  }

  &__actions {
    display: flex;
    gap: 10px;
    padding: 12px 16px 16px;
    border-top: 1px solid $border-color;
  }
}

.filter-tabs {
  display: flex;
  gap: 4px;
  padding: 12px 16px 0;

  &__tab {
    flex: 1;
    padding: 8px 10px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    font-size: 13px;
    font-weight: 500;
    color: $text-muted;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: $bg-subtle;
      color: $text-primary;
    }

    &--active,
    &--active:hover {
      background: $bg-subtle;
      color: $text-primary;
      font-weight: 600;
    }

    &:focus-visible {
      outline: 2px solid $accent-primary;
      outline-offset: 1px;
    }
  }
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: $bg-subtle;
  }

  &:last-child {
    margin-bottom: 0;
  }

  &__indicator {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;

    &--pending {
      background: $warning;
    }

    &--active,
    &--approved,
    &--completed {
      background: $success;
    }

    &--rejected,
    &--failed {
      background: $error;
    }

    &--terminated {
      background: $text-muted;
    }
  }

  &__label {
    flex: 1;
    font-size: 14px;
    color: $text-primary;
  }

  &__radio {
    display: none;
  }

  &__check {
    width: 20px;
    height: 20px;
    border: 2px solid $border-color;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &--active {
      background: $accent-primary;
      border-color: $accent-primary;
      color: white;
    }
  }
}

.filter-btn {
  flex: 1;
  padding: 10px 16px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &--secondary {
    background: $bg-subtle;
    border: 1px solid $border-color;
    color: $text-secondary;

    &:hover {
      background: darken($bg-subtle, 3%);
    }
  }

  &--primary {
    background: $btn-primary;
    border: none;
    color: white;

    &:hover:not(:disabled) {
      background: $btn-primary-hover;
    }

    &:disabled {
      background: $bg-subtle;
      color: $text-muted;
      cursor: not-allowed;
    }
  }
}

@keyframes dropdownFade {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
